/**
 * Drop-in replacement for `next/image` backed by a plain <img>. Vite serves
 * imported/static assets as URL strings, so there is nothing to optimize; the
 * Next-only props (priority/fill/sizes/quality/placeholder/loader/unoptimized)
 * are accepted and ignored.
 */
import type { ImgHTMLAttributes } from "react";

// In Vite, importing an image yields a string URL. Keep the name for callers
// that previously imported `StaticImageData` from next/image.
export type StaticImageData = string;

type NextImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src: string | { src: string };
  priority?: boolean;
  fill?: boolean;
  quality?: number;
  sizes?: string;
  placeholder?: string;
  unoptimized?: boolean;
  loader?: unknown;
};

export default function Image({
  src,
  priority: _priority,
  fill,
  quality: _quality,
  placeholder: _placeholder,
  unoptimized: _unoptimized,
  loader: _loader,
  style,
  ...rest
}: NextImageProps) {
  const resolvedSrc = typeof src === "string" ? src : src?.src;
  const fillStyle = fill
    ? { position: "absolute" as const, inset: 0, width: "100%", height: "100%", objectFit: "cover" as const }
    : undefined;
  return <img src={resolvedSrc} style={{ ...fillStyle, ...style }} {...rest} />;
}
